import { useState } from 'react'

export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced'
export type LearningGoal = 'job' | 'freelance' | 'upskill' | 'portfolio'
export type LearningStyle = 'visual' | 'hands-on' | 'theory' | 'mixed'
export type TimeCommitment = 'full-time' | 'part-time' | 'flexible'
export type Interest =
  | 'ui'
  | 'ux'
  | 'research'
  | 'systems'
  | 'motion'
  | 'strategy'

export interface OnboardingData {
  name: string
  email: string
  experienceLevel: ExperienceLevel | null
  learningGoal: LearningGoal | null
  learningStyle: LearningStyle | null
  timeCommitment: TimeCommitment | null
  interests: Interest[]
}

const initialData: OnboardingData = {
  name: '',
  email: '',
  experienceLevel: null,
  learningGoal: null,
  learningStyle: null,
  timeCommitment: null,
  interests: [],
}

export function useOnboarding() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [data, setData] = useState<OnboardingData>(initialData)

  const totalSteps = 7

  const openOnboarding = () => {
    setIsOpen(true)
    setCurrentStep(0)
  }

  const closeOnboarding = () => setIsOpen(false)

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const updateData = (updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }))
  }

  const toggleInterest = (interest: Interest) => {
    setData((prev) => {
      const exists = prev.interests.includes(interest)
      return {
        ...prev,
        interests: exists
          ? prev.interests.filter((i) => i !== interest)
          : [...prev.interests, interest],
      }
    })
  }

  const getRecommendation = () => {
    // Simple logic to generate a recommendation based on inputs
    if (data.experienceLevel === 'beginner') {
      return {
        track: 'Product Design Foundation',
        duration: '12 Weeks',
        focus: 'End-to-end design process',
        match: 98,
      }
    } else if (data.learningGoal === 'freelance') {
      return {
        track: 'Freelance & Business of Design',
        duration: '8 Weeks',
        focus: 'Client acquisition & pricing',
        match: 95,
      }
    } else if (data.interests.includes('systems')) {
      return {
        track: 'Advanced Design Systems',
        duration: '6 Weeks',
        focus: 'Scalable architecture & tokens',
        match: 92,
      }
    } else {
      return {
        track: 'Senior Product Designer',
        duration: '16 Weeks',
        focus: 'Leadership & strategy',
        match: 94,
      }
    }
  }

  return {
    isOpen,
    openOnboarding,
    closeOnboarding,
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    data,
    updateData,
    toggleInterest,
    getRecommendation,
  }
}
